<template>
  <div class="pv-overlay" :class="{ 'pv-pier': target.site === 'rbxpier' }" @click.self="close">
    <div class="pv-modal balance-modal">
      <button class="pv-modal__close" @click="close" :disabled="isSending" aria-label="Закрыть">×</button>
      <h2 class="pv-modal__title">Изменить баланс</h2>
      <p class="pv-modal__subtitle balance-modal__who">
        {{ siteName }} ·
        <b>{{ target.username || "пользователь" }}</b>
        <IdCell v-if="userId" :value="userId" :tail="24" />
      </p>

      <!-- Баланс сейчас и превью «станет» — видно до подтверждения -->
      <div class="balance-modal__wallet" v-if="hasBalance && step !== 'result'">
        <div class="balance-modal__wallet-col">
          <span class="pv-label">Баланс сейчас</span>
          <span class="balance-modal__wallet-value">{{ formatRub(target.balance) }}</span>
        </div>
        <template v-if="hasPreview">
          <span class="balance-modal__wallet-arrow">→</span>
          <div class="balance-modal__wallet-col">
            <span class="pv-label">Станет</span>
            <span class="balance-modal__wallet-value" :class="mode === 'credit' ? 'is-credit' : 'is-debit'">
              {{ formatRub(balanceAfter) }}
            </span>
          </div>
        </template>
      </div>

      <!-- Шаг 1: форма -->
      <template v-if="step === 'form'">
        <div class="pv-alert pv-alert--warning" v-if="isForeignCurrency">
          Основная валюта пользователя — {{ target.currency }}. Сумма вводится в рублях и меняет рублёвый счёт
          <template v-if="hasBalance">({{ formatRub(target.balance) }})</template>
          .
        </div>
        <div class="pv-section" v-if="!target.userId">
          <label class="pv-label" for="balance-user-id">User ID</label>
          <input
            id="balance-user-id"
            v-model.trim="manualUserId"
            class="pv-input pv-mono"
            :class="{ 'is-invalid': touched && errors.userId }"
            placeholder="602edb834e32ad44efb8bd08"
          />
          <p class="pv-hint is-error" v-if="touched && errors.userId">{{ errors.userId }}</p>
        </div>

        <div class="pv-section">
          <div class="pv-segmented">
            <button class="is-credit" :class="{ 'is-active': mode === 'credit' }" @click="mode = 'credit'">
              + Начислить
            </button>
            <button class="is-debit" :class="{ 'is-active': mode === 'debit' }" @click="mode = 'debit'">
              − Списать
            </button>
          </div>
        </div>

        <div class="pv-section">
          <label class="pv-label" for="balance-amount">Сумма, ₽</label>
          <div class="balance-modal__amount-wrap" :class="mode === 'credit' ? 'is-credit' : 'is-debit'">
            <span class="balance-modal__sign">{{ mode === "credit" ? "+" : "−" }}</span>
            <input
              id="balance-amount"
              ref="amountInput"
              v-model.trim="amountInput"
              class="pv-input balance-modal__amount"
              :class="{ 'is-invalid': touched && errors.amount }"
              inputmode="decimal"
              placeholder="0"
              @keydown.enter="goConfirm"
            />
          </div>
          <div class="pv-chips">
            <button
              v-for="preset in presets"
              :key="preset"
              class="pv-chip"
              :class="{ 'is-active': amount === preset }"
              @click="amountInput = String(preset)"
            >
              {{ formatRub(preset) }}
            </button>
            <button
              v-if="mode === 'debit' && hasBalance && target.balance > 0"
              class="pv-chip"
              :class="{ 'is-active': amount === maxDebit }"
              @click="amountInput = String(maxDebit)"
            >
              Весь баланс
            </button>
          </div>
          <p class="pv-hint is-error" v-if="touched && errors.amount">{{ errors.amount }}</p>
          <p class="pv-hint" v-else>
            Не больше {{ formatRub(limits.perRequest) }} за раз. Начисления: до {{ formatRub(limits.perUserDaily) }} на
            пользователя и {{ formatRub(limits.totalDaily) }} на всех в сутки (UTC).
          </p>
        </div>

        <div class="pv-section">
          <label class="pv-label" for="balance-reason">
            Причина
            <span class="balance-modal__required">*</span>
          </label>
          <textarea
            id="balance-reason"
            v-model="reason"
            class="pv-textarea"
            :class="{ 'is-invalid': touched && errors.reason }"
            placeholder="Например: робуксы не пришли по заказу …e33d8"
          ></textarea>
          <div class="pv-chips">
            <button
              v-for="template in reasonTemplates"
              :key="template"
              class="pv-chip"
              @click="applyTemplate(template)"
            >
              {{ template }}
            </button>
          </div>
          <p class="pv-hint is-error" v-if="touched && errors.reason">{{ errors.reason }}</p>
        </div>

        <div class="pv-actions">
          <button class="pv-btn pv-btn--ghost" @click="close">Отмена</button>
          <button class="pv-btn" :class="mode === 'credit' ? 'pv-btn--primary' : 'pv-btn--danger'" @click="goConfirm">
            Далее
          </button>
        </div>
      </template>

      <!-- Шаг 2: подтверждение -->
      <template v-if="step === 'confirm'">
        <div class="pv-alert pv-alert--warning">Проверьте данные. Операция выполняется сразу и пишется в лог.</div>
        <div class="pv-summary">
          <div class="pv-summary__row">
            <span>Операция</span>
            <span class="pv-summary__amount" :class="mode === 'credit' ? 'is-credit' : 'is-debit'">
              {{ mode === "credit" ? "+" : "−" }}{{ formatRub(amount) }}
            </span>
          </div>
          <div class="pv-summary__row">
            <span>Проект</span>
            <span>{{ siteName }}</span>
          </div>
          <div class="pv-summary__row">
            <span>Пользователь</span>
            <span>
              {{ target.username || "—" }}
              <span class="pv-mono balance-modal__id">{{ userId }}</span>
            </span>
          </div>
          <div class="pv-summary__row">
            <span>Причина</span>
            <span>{{ reason.trim() }}</span>
          </div>
        </div>
        <div class="pv-actions">
          <button class="pv-btn pv-btn--ghost" :disabled="isSending" @click="step = 'form'">Назад</button>
          <button
            class="pv-btn"
            :class="mode === 'credit' ? 'pv-btn--primary' : 'pv-btn--danger'"
            :disabled="isSending"
            @click="submit"
          >
            <template v-if="isSending">Отправляю…</template>
            <template v-else>{{ mode === "credit" ? "Начислить" : "Списать" }} {{ formatRub(amount) }}</template>
          </button>
        </div>
      </template>

      <!-- Шаг 3: результат -->
      <template v-if="step === 'result'">
        <div class="pv-alert" :class="result.ok ? 'pv-alert--success' : 'pv-alert--error'">
          <template v-if="result.ok">
            Готово: {{ mode === "credit" ? "начислено" : "списано" }} {{ formatRub(amount) }}.
          </template>
          <template v-else>{{ result.message }}</template>
        </div>
        <div class="pv-actions">
          <template v-if="result.ok">
            <button class="pv-btn pv-btn--primary" @click="close">Готово</button>
          </template>
          <template v-else-if="result.code === 401">
            <button class="pv-btn pv-btn--ghost" @click="close">Закрыть</button>
            <button class="pv-btn pv-btn--primary" @click="reenterKey">Ввести ключ</button>
          </template>
          <template v-else>
            <button class="pv-btn pv-btn--ghost" @click="close">Закрыть</button>
            <button class="pv-btn pv-btn--primary" @click="step = 'form'">Изменить и повторить</button>
          </template>
        </div>
      </template>

      <div class="pv-journal" v-if="siteJournal.length">
        <span class="pv-label">Операции за эту сессию</span>
        <div class="pv-journal__item" v-for="entry in siteJournal" :key="entry.time" :title="entry.reason">
          <span class="pv-journal__time">{{ formatTime(entry.time) }}</span>
          <span class="pv-journal__reason">
            {{ entry.username || entry.userId }} — {{ entry.reason }}
            <template v-if="!entry.ok">({{ entry.code || "нет ответа" }})</template>
          </span>
          <span
            class="pv-journal__amount"
            :class="{ 'is-credit': entry.amount > 0, 'is-debit': entry.amount < 0, 'is-failed': !entry.ok }"
          >
            {{ entry.amount > 0 ? "+" : "−" }}{{ formatRub(Math.abs(entry.amount)) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@/scss/_privileged.scss";
import "./balanceModal.scss";

import { SITE_NAMES, BALANCE_LIMITS, changeBalance, readJournal, addJournalEntry } from "@/utils/privileged";

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const rubFormat = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });

export default {
  name: "BalanceModal",
  data() {
    return {
      step: "form",
      mode: "credit",
      amountInput: "",
      reason: "",
      manualUserId: "",
      touched: false,
      isSending: false,
      result: null,
      journal: readJournal(),
      limits: BALANCE_LIMITS,
      presets: [100, 300, 500, 1000, 3000],
      reasonTemplates: ["Возврат за заказ ", "Компенсация: ", "Ошибочное начисление", "Додеп не зачислился: "]
    };
  },
  computed: {
    target() {
      return this.$store.state.balanceTarget;
    },
    siteName() {
      return SITE_NAMES[this.target.site];
    },
    userId() {
      return this.target.userId || this.manualUserId;
    },
    isForeignCurrency() {
      return Boolean(this.target.currency) && String(this.target.currency).toUpperCase() !== "RUB";
    },
    hasBalance() {
      return typeof this.target.balance === "number" && !Number.isNaN(this.target.balance);
    },
    amount() {
      const value = Number(String(this.amountInput).replace(",", ".").replace(/\s/g, ""));
      return Number.isFinite(value) ? Math.round(value * 100) / 100 : NaN;
    },
    signedAmount() {
      return this.mode === "credit" ? this.amount : -this.amount;
    },
    balanceAfter() {
      return Math.round((this.target.balance + this.signedAmount) * 100) / 100;
    },
    // Превью «станет» — как только введена осмысленная сумма
    hasPreview() {
      return this.hasBalance && Number.isFinite(this.amount) && this.amount > 0;
    },
    maxDebit() {
      return Math.min(this.target.balance, BALANCE_LIMITS.perRequest);
    },
    errors() {
      const errors = {};
      if (!OBJECT_ID.test(this.userId)) {
        errors.userId = "Нужен ObjectId пользователя — 24 символа 0-9 a-f";
      }
      if (!this.amountInput || Number.isNaN(this.amount) || this.amount <= 0) {
        errors.amount = "Введите сумму больше нуля";
      } else if (this.amount > BALANCE_LIMITS.perRequest) {
        errors.amount = `За один раз — не больше ${this.formatRub(BALANCE_LIMITS.perRequest)}`;
      } else if (this.mode === "debit" && this.hasBalance && this.amount > this.target.balance) {
        errors.amount = `Нельзя списать больше текущего баланса (${this.formatRub(this.target.balance)})`;
      }
      if (this.reason.trim().length < 5) {
        errors.reason = "Опишите причину — минимум 5 символов";
      }
      return errors;
    },
    siteJournal() {
      return this.journal.filter((entry) => entry.site === this.target.site).slice(0, 5);
    }
  },
  methods: {
    formatRub(value) {
      return `${rubFormat.format(value)} ₽`;
    },
    formatTime(iso) {
      return new Date(iso).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    },
    applyTemplate(template) {
      this.reason = this.reason.trim() ? `${this.reason.trim()} ${template}` : template;
    },
    goConfirm() {
      this.touched = true;
      if (Object.keys(this.errors).length) return;
      this.step = "confirm";
    },
    async submit() {
      if (this.isSending) return;
      this.isSending = true;

      const result = await changeBalance(this.target.site, {
        userId: this.userId,
        amount: this.signedAmount,
        reason: this.reason.trim()
      });

      this.journal = addJournalEntry({
        site: this.target.site,
        userId: this.userId,
        username: this.target.username,
        amount: this.signedAmount,
        reason: this.reason.trim(),
        ok: result.ok,
        code: result.code
      });

      this.result = result;
      this.isSending = false;
      this.step = "result";

      if (result.ok && typeof this.target.onChanged === "function") {
        this.target.onChanged();
      }
    },
    reenterKey() {
      this.close();
      this.$store.state.isOpenPrivilegedLogin = true;
    },
    close() {
      if (this.isSending) return;
      this.$store.state.balanceTarget = null;
    }
  },
  mounted() {
    this.$nextTick(() => this.$refs.amountInput && this.$refs.amountInput.focus());
  }
};
</script>
